import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaSearch, FaFilter, FaTimes } from 'react-icons/fa';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';

// Components
import SectionTitle from '../components/SectionTitle';
import ProductCard from '../components/products/ProductCard';
import Button from '../components/Button';

const CategoriesPage = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const categoryParam = queryParams.get('category') || queryParams.get('type');
  
  const [selectedCategory, setSelectedCategory] = useState(categoryParam || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [sortOption, setSortOption] = useState('featured');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState(['all']);
  
  // Update selected category when URL changes
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const categoryParam = queryParams.get('category') || queryParams.get('type');
    setSelectedCategory(categoryParam || 'all');
  }, [location.search]);
  
  // Fetch all products from Firestore
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsCollection = collection(db, 'products');
        const productsSnapshot = await getDocs(productsCollection);
        
        const productsList = productsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setAllProducts(productsList);
        
        // Extract unique categories from products
        const uniqueCategories = ['all', ...new Set(productsList.map(product => 
          product.category ? product.category : '').filter(Boolean))];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, []);
  
  // Filter products based on selected category, search query, and price range
  useEffect(() => {
    if (allProducts.length === 0) return;
    
    let filtered = [...allProducts];
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(
        product => product.category && product.category.toLowerCase() === selectedCategory.toLowerCase()
      );
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        product => 
          (product.name && product.name.toLowerCase().includes(query)) || 
          (product.description && product.description.toLowerCase().includes(query))
      );
    }
    
    // Filter by price range
    filtered = filtered.filter(
      product => product.price && product.price >= priceRange[0] && product.price <= priceRange[1]
    );
    
    // Sort products
    switch (sortOption) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        // 'featured' - prioritize featured products
        filtered.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
        break;
    }
    
    setFilteredProducts(filtered);
  }, [allProducts, selectedCategory, searchQuery, sortOption, priceRange]);
  
  // Handle category change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setIsFilterOpen(false);
  };
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };
  
  // Handle sort option change
  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };
  
  // Handle price range change
  const handlePriceRangeChange = (e, index) => {
    const newRange = [...priceRange];
    newRange[index] = Number(e.target.value);
    setPriceRange(newRange);
  };
  
  // Toggle filter sidebar on mobile
  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen);
  };
  
  return (
    <PageContainer>
      <PageHeader>
        <Container>
          <SectionTitle 
            title={selectedCategory === 'all' ? 'All Products' : selectedCategory} 
            subtitle="Browse our extensive collection of premium collectibles"
          />
          
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <SearchIcon>
              <FaSearch />
            </SearchIcon>
          </SearchContainer>
          
          <MobileFilterButton onClick={toggleFilter}>
            <FaFilter /> Filters
          </MobileFilterButton>
        </Container>
      </PageHeader>
      
      <Container>
        <ContentContainer>
          <FilterSidebar $isOpen={isFilterOpen}>
            <FilterHeader>
              <FilterTitle>Filters</FilterTitle>
              <CloseFilterButton onClick={toggleFilter}>
                <FaTimes />
              </CloseFilterButton>
            </FilterHeader>
            
            <FilterSection>
              <FilterSectionTitle>Categories</FilterSectionTitle>
              <CategoryList>
                {categories.map(category => (
                  <CategoryItem 
                    key={category}
                    $isActive={selectedCategory === category}
                    onClick={() => handleCategoryChange(category)}
                  >
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </CategoryItem>
                ))}
              </CategoryList>
            </FilterSection>
            
            <FilterSection>
              <FilterSectionTitle>Price Range</FilterSectionTitle>
              <PriceInputs>
                <PriceInput>
                  <label>Min:</label>
                  <input
                    type="number"
                    min="0"
                    max={priceRange[1]}
                    value={priceRange[0]}
                    onChange={(e) => handlePriceRangeChange(e, 0)}
                  />
                </PriceInput>
                <PriceInput>
                  <label>Max:</label>
                  <input
                    type="number"
                    min={priceRange[0]}
                    value={priceRange[1]}
                    onChange={(e) => handlePriceRangeChange(e, 1)}
                  />
                </PriceInput>
              </PriceInputs>
              <PriceSlider
                type="range"
                min="0"
                max="5000"
                value={priceRange[1]}
                onChange={(e) => handlePriceRangeChange(e, 1)}
              />
            </FilterSection>
          </FilterSidebar>
          
          <ProductsContainer>
            <ProductsHeader>
              <ResultCount>
                {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'} found
              </ResultCount>
              <SortContainer>
                <SortLabel>Sort by:</SortLabel>
                <SortSelect value={sortOption} onChange={handleSortChange}>
                  <option value="featured">Featured</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="name-asc">Name: A to Z</option>
                  <option value="name-desc">Name: Z to A</option>
                </SortSelect>
              </SortContainer>
            </ProductsHeader>
            
            {loading ? (
              <LoadingMessage>Loading products...</LoadingMessage>
            ) : filteredProducts.length > 0 ? (
              <ProductGrid>
                {filteredProducts.map(product => (
                  <ProductItem key={product.id}>
                    <ProductCard product={product} />
                  </ProductItem>
                ))}
              </ProductGrid>
            ) : (
              <EmptyState>
                <EmptyStateTitle>No products found</EmptyStateTitle>
                <EmptyStateText>
                  Try adjusting your filters or search query to find what you're looking for.
                </EmptyStateText>
                <Button onClick={() => {
                  setSelectedCategory('all');
                  setSearchQuery('');
                  setPriceRange([0, 5000]);
                }}>
                  Reset Filters
                </Button>
              </EmptyState>
            )}
          </ProductsContainer>
        </ContentContainer>
      </Container>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
`;

const PageHeader = styled.div`
  background: ${({ theme }) => theme.gradients.darkBlue};
  padding: 2rem 0;
  color: ${({ theme }) => theme.colors.text.primary};
  border-bottom: 2px solid ${({ theme }) => theme.colors.metallic.gold};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 1.5rem 0;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 1rem 0.5rem;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  max-width: 600px;
  margin: 2rem auto;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: ${({ theme }) => theme.colors.background.light};
  font-size: 1rem;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 1rem;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.text.muted};
`;

const MobileFilterButton = styled.button`
  display: none;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  margin: 0 auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
  }
`;

const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 2rem;
  margin: 3rem 0;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin: 1.5rem 0;
  }
`;

const FilterSidebar = styled.div`
  padding-right: 1.5rem;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  box-sizing: border-box;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    position: fixed;
    top: 0;
    left: 0;
    width: 70%;
    max-width: 300px;
    height: 100vh;
    background-color: ${({ theme }) => theme.colors.background.light};
    z-index: 1000;
    padding: 1.5rem;
    box-shadow: ${({ theme }) => theme.shadows.lg};
    overflow-y: auto;
    transform: ${({ $isOpen }) => ($isOpen ? 'translateX(0)' : 'translateX(-100%)')};
    transition: transform ${({ theme }) => theme.transitions.medium};
  }
`;

const FilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const FilterTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
`;

const CloseFilterButton = styled.button`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.text.muted};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const FilterSection = styled.div`
  margin-bottom: 2rem;
`;

const FilterSectionTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const CategoryList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CategoryItem = styled.li`
  cursor: pointer;
  padding: 0.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: all ${({ theme }) => theme.transitions.short};
  background-color: ${({ $isActive, theme }) => 
    $isActive ? theme.colors.primary : 'transparent'};
  color: ${({ $isActive, theme }) => 
    $isActive ? theme.colors.text.primary : theme.colors.text.dark};
  
  &:hover {
    background-color: ${({ $isActive, theme }) => 
      $isActive ? theme.colors.primary : theme.colors.background.medium};
    color: ${({ $isActive, theme }) => 
      $isActive ? theme.colors.text.primary : theme.colors.text.primary};
  }
`;

const PriceInputs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  max-width: 100%;
  overflow: hidden;
`;

const PriceInput = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex: 1;
  
  label {
    font-size: 0.85rem;
    white-space: nowrap;
  }
  
  input {
    width: 70px;
    padding: 0.5rem 0.4rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    font-size: 0.9rem;
    box-sizing: border-box;
    
    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const PriceSlider = styled.input`
  width: 100%;
  margin-top: 1rem;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
  }
  
  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
  }
`;

const ProductsSection = styled.div`
  flex: 1;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow-x: hidden;
`;

const ProductsContainer = styled.div`
  padding-left: 1rem;
`;

const ProductsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const ResultCount = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const SortContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SortLabel = styled.label`
  font-size: 1rem;
`;

const SortSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background-color: ${({ theme }) => theme.colors.background.light};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }
`;

const ProductItem = styled.div`
  height: 100%;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  background-color: #f9f9f9;
  border-radius: 8px;
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.text.dark};
`;

const EmptyStateText = styled.p`
  font-size: 1rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export default CategoriesPage;
